import { AbstractControl, ValidationErrors, Validators } from '@angular/forms';
import { PASSWORD_ERROR_MESSAGES } from '@epicuro-next/constants/features/localization';
import {
  passwordLetterRequired,
  passwordLowercaseRequired,
  passwordNumericalCharRequired,
  passwordsDontMatch,
  passwordSpecialCharRequired,
  passwordsSame,
  passwordUppercaseRequired,
} from './password-validators/password-validators';

export const formFields = {
  oldPassword: {
    placeholder: $localize`:UserMenu|placeholder@@changePasswordFormOldPassword:Old password`,
    e2e: 'old-password',
    control: 'oldPassword',
    specificErrors: ['passwordsSame'],
    noValidityClass: true,
    autocomplete: 'current-password',
  },
  newPassword: {
    placeholder: $localize`:UserMenu|placeholder@@changePasswordFormNewPassword:New password`,
    e2e: 'new-password',
    control: 'newPassword',
    specificErrors: [
      'passwordsSame',
      'passwordsDontMatch',
      'passwordLetterRequired',
      'passwordUppercaseRequired',
      'passwordLowercaseRequired',
      'passwordSpecialCharRequired',
    ],
    noValidityClass: false,
    autocomplete: 'new-password',
  },
  confirmPassword: {
    placeholder: $localize`:UserMenu|placeholder@@changePasswordFormConfirmNewPassword:Confirm new password`,
    e2e: 'confirm-password',
    control: 'confirmPassword',
    specificErrors: ['passwordsDontMatch'],
    noValidityClass: false,
    autocomplete: 'new-password',
  },
};

// This helps dynamically render the form fields in the template
export const formFieldsArr = Object.values(formFields);

// Extracts specific errors from formFields to generate fieldErrorMap dynamically
export const fieldErrorMap: Record<string, string[]> = Object.keys(
  formFields,
).reduce((acc, key) => {
  acc[key] = (formFields as any)[key].specificErrors || [];
  return acc;
}, {} as Record<string, string[]>);

export const sharedPasswordValidators = [
  passwordsDontMatch,
  passwordsSame,
  passwordLetterRequired,
  passwordUppercaseRequired,
  passwordLowercaseRequired,
  passwordSpecialCharRequired,
  passwordNumericalCharRequired,
  Validators.minLength(12),
];

/**
 * Helper to build a list of error messages from a control or form
 */
export function getErrorMessages(
  errors: ValidationErrors | null | undefined,
  errorMap: Record<string, string> = PASSWORD_ERROR_MESSAGES,
): string[] {
  if (!errors) return [];
  return Object.keys(errors)
    .filter((key) => errorMap[key]) // Only map known errors
    .map((key) => errorMap[key]);
}

/**
 * Helper to apply a validity CSS class
 */
export function getValidityClass(
  control: AbstractControl | null,
  errors: ValidationErrors | null | undefined,
): string | null {
  // Avoid marking invalid until user touches the control (or your preference)
  if (!control || (errors?.['required'] && !control.touched)) {
    return null;
  }
  return !errors || Object.keys(errors).length === 0
    ? 'is-valid'
    : 'is-invalid';
}

/**
 * Merges control-level errors with form-level errors that are "specific" to a field
 */
export function mergeControlWithFormErrors(
  control: AbstractControl | null,
  formErrors: ValidationErrors | null,
  specificErrors: string[] = [],
) {
  if (!control) return null;

  const merged = { ...control.errors };
  if (formErrors) {
    Object.keys(formErrors).forEach((key) => {
      if (specificErrors.includes(key)) {
        merged[key] = true;
      }
    });
  }
  return Object.keys(merged).length ? merged : null;
}
